import { useCallback, useEffect, useState } from 'react'
import { IDropdownData, TypeOrNull } from 'models/Common'

export const amountLiftedFrequentlyDropdownData = [
  {
    value: 50,
    label: '50lbs',
  },
  {
    value: 45,
    label: '45lbs',
  },
  {
    value: 40,
    label: '40lbs',
  },
  {
    value: 35,
    label: '35lbs',
  },
  {
    value: 30,
    label: '30lbs',
  },
  {
    value: 25,
    label: '25lbs',
  },
  {
    value: 20,
    label: '20lbs',
  },
  {
    value: 15,
    label: '15lbs',
  },
  {
    value: 10,
    label: '10lbs',
  },
  {
    value: 5,
    label: '5lbs',
  },
]

export const amountLiftedOccasionallyDropdownData = [
  { value: 50, label: '50lbs' },
  {
    value: 45,
    label: '45lbs',
  },
  {
    value: 40,
    label: '40lbs',
  },
  {
    value: 35,
    label: '35lbs',
  },
  {
    value: 30,
    label: '30lbs',
  },
  {
    value: 25,
    label: '25lbs',
  },
  {
    value: 20,
    label: '20lbs',
  },
  {
    value: 15,
    label: '15lbs',
  },
  {
    value: 10,
    label: '10lbs',
  },
  {
    value: 5,
    label: '5lbs',
  },
]

export const amountOfTimeSpentOnFeetDropdownData = [
  {
    value: 100,
    label: '100%',
  },
  {
    value: 75,
    label: '75%',
  },
  {
    value: 50,
    label: '50%',
  },
  {
    value: 25,
    label: '25%',
  },
]

export const DEFAULT_AMOUNT_LIFTED_FREQUENTLY = amountLiftedFrequentlyDropdownData[9]
export const DEFAULT_AMOUNT_LIFTED_OCCASIONALLY = amountLiftedOccasionallyDropdownData[8]
export const DEFAULT_AMOUNT_OF_TIME_SPENT_ON_FEET = amountOfTimeSpentOnFeetDropdownData[0]

/**
 * Return the desired dropdown options array depending on the passed fieldName
 * @param fieldName
 * @returns
 */
const getOptionsFromFieldName = (fieldName: string) => {
  if (fieldName === 'amount_lifted_frequently') return amountLiftedFrequentlyDropdownData
  if (fieldName === 'amount_lifted_occasionally') return amountLiftedOccasionallyDropdownData
  if (fieldName === 'amount_of_time_spent_on_feet') return amountOfTimeSpentOnFeetDropdownData

  // eslint-disable-next-line no-console
  console.warn('Please specify a correct fieldName in the usePhysicalRequirementsDropdown hook.')
  return []
}

/**
 * Return a DropdownOption depending on the fieldName and the fieldValue
 * @param fieldName
 * @param fieldValue
 * @returns
 */
export const mapFieldFromValue = (fieldName: string, fieldValue: number) => {
  const dropdownData = getOptionsFromFieldName(fieldName)
  const selectedOption = dropdownData.find((dropdownItem) => fieldValue === dropdownItem.value)
  return selectedOption as IDropdownData
}

export const mapFieldFromValueToDropdownData = (
  fieldValue: number,
  dropdownData: IDropdownData[]
) => {
  const selectedOption = dropdownData.find((dropdownItem) => fieldValue === dropdownItem.value)
  return selectedOption || null
}

export const useAmountFrequentlyDropdown = (
  setFieldValue: (field: string, value: any) => void,
  initialValue: TypeOrNull<number>
) => {
  const [currentAmountLiftedFrequently, setCurrentAmountLiftedFrequently] = useState<
    TypeOrNull<IDropdownData>
  >(
    !!initialValue
      ? mapFieldFromValueToDropdownData(initialValue, amountLiftedFrequentlyDropdownData)
      : null
  )

  const handleAmountFrequentlyDropdownChange = useCallback(
    (field: string, selected: IDropdownData) => {
      setFieldValue(field, selected.value)
      setCurrentAmountLiftedFrequently(selected)
    },
    [setFieldValue]
  )

  useEffect(() => {
    setFieldValue('amount_lifted_frequently', !!initialValue ? initialValue : null)
  }, [initialValue, setFieldValue])

  return { currentAmountLiftedFrequently, handleAmountFrequentlyDropdownChange }
}

export const useAmountOccasionallyDropdown = (
  setFieldValue: (field: string, value: any) => void,
  initialValue: TypeOrNull<number>
) => {
  const [currentAmountLiftedOccasionally, setCurrentAmountLiftedOccasionally] = useState<
    TypeOrNull<IDropdownData>
  >(
    !!initialValue
      ? mapFieldFromValueToDropdownData(initialValue, amountLiftedOccasionallyDropdownData)
      : null
  )

  const handleAmountOccasionallyDropdownChange = useCallback(
    (field: string, selected: IDropdownData) => {
      setFieldValue(field, selected.value)
      setCurrentAmountLiftedOccasionally(selected)
    },
    [setFieldValue]
  )

  useEffect(() => {
    setFieldValue('amount_lifted_occasionally', !!initialValue ? initialValue : null)
  }, [setFieldValue, initialValue])

  return {
    currentAmountLiftedOccasionally,
    handleAmountOccasionallyDropdownChange,
  }
}

export const useAmountOnFeetDropdown = (
  setFieldValue: (field: string, value: any) => void,
  initialValue: TypeOrNull<string>
) => {
  const [currentAmountOfTimeSpentOnFeet, setCurrentAmountOfTimeSpentOnFeet] = useState<
    TypeOrNull<IDropdownData>
  >(
    initialValue !== null
      ? mapFieldFromValueToDropdownData(Number(initialValue), amountOfTimeSpentOnFeetDropdownData)
      : null
  )

  const handleTimeOnFeetDropdownChange = useCallback(
    (field: string, selected: IDropdownData) => {
      setFieldValue(field, selected.value)
      setCurrentAmountOfTimeSpentOnFeet(selected)
    },
    [setFieldValue]
  )

  useEffect(() => {
    setFieldValue(
      'amount_of_time_spent_on_feet',
      initialValue !== null ? Number(initialValue) : null
    )
  }, [setFieldValue, initialValue])

  return { currentAmountOfTimeSpentOnFeet, handleTimeOnFeetDropdownChange }
}
